import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { OWNERSHIP_VALUES } from "@/types/models";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UseFormRegister, Control, Controller, UseFormGetValues, UseFormSetValue } from "react-hook-form";
import TagsInput from "@/components/TagsInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSourceTypes, useEvidenceTypes, useNetsuiteClients } from "@/hooks/useMasterData";
import TopicsMultiSelect from "@/components/TopicsMultiSelect";
import { DatePicker } from "@/components/ui/date-picker";

export interface MetadataFormValues {
  title: string;
  authors: string;
  year: string;
  description: string;
  source_type: string;
  organization: string;
  country: string;
  evidence_type: string;
  data_retention: string | null;
  url: string;
  language: string;
  ownership: string;
  client_id: string;
  client_name: string;
  tags: string;
  topic_ids: string[];
}

export const emptyMetadata: MetadataFormValues = {
  title: "",
  authors: "",
  year: "",
  description: "",
  source_type: "",
  organization: "",
  country: "",
  evidence_type: "",
  data_retention: null,
  url: "",
  language: "",
  ownership: "Private",
  client_id: "",
  client_name: "",
  tags: "",
  topic_ids: [],
};

interface Props {
  register: UseFormRegister<MetadataFormValues>;
  control: Control<MetadataFormValues>;
  getValues: UseFormGetValues<MetadataFormValues>;
  setValue: UseFormSetValue<MetadataFormValues>;
  idPrefix?: string;
  readOnlyOwnership?: boolean;
  /** When set, the client field shows read-only with this name (project has a locked client) */
  lockedClientName?: string;
  /** Restrict which ownership values appear in the dropdown. Defaults to all three. */
  allowedOwnershipValues?: string[];
}

export interface DocumentMetadataFieldsHandle {
  /** Scrolls to a field with unsaved-but-invalid input (e.g. a half-typed date). Returns true if it found one. */
  focusInvalidField: () => boolean;
}

const DocumentMetadataFields = forwardRef<DocumentMetadataFieldsHandle, Props>(({
  register,
  control,
  getValues,
  setValue,
  idPrefix = "meta",
  readOnlyOwnership = false,
  lockedClientName,
  allowedOwnershipValues,
}, ref) => {
  const { data: sourceTypes = [] } = useSourceTypes();
  const { data: evidenceTypes = [] } = useEvidenceTypes();

  const activeSourceTypes = sourceTypes.filter(st => st.is_active);
  const activeEvidenceTypes = evidenceTypes.filter(et => et.is_active);

  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);
  const [clientInputValue, setClientInputValue] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const clientDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [dataRetentionInvalid, setDataRetentionInvalid] = useState(false);
  const dataRetentionRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    focusInvalidField: () => {
      if (dataRetentionInvalid) {
        dataRetentionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        return true;
      }
      return false;
    },
  }), [dataRetentionInvalid]);
  const { data: clientResults = [], isFetching: clientFetching } = useNetsuiteClients(clientSearch);

  return (
  <div className="grid grid-cols-2 gap-4">
    <div className="col-span-2 space-y-1.5">
      <Label htmlFor={`${idPrefix}-title`}>Title</Label>
      <Input id={`${idPrefix}-title`} placeholder="Document title" {...register("title")} />
    </div>

    <div className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-authors`}>Author</Label>
      <Input id={`${idPrefix}-authors`} placeholder="Author name" {...register("authors")} />
    </div>

    <div className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-year`}>Year</Label>
      <Input id={`${idPrefix}-year`} type="number" placeholder="e.g. 2024" {...register("year")} />
    </div>

    <div className="col-span-2 space-y-1.5">
      <Label htmlFor={`${idPrefix}-description`}>Description</Label>
      <Textarea id={`${idPrefix}-description`} placeholder="Brief description of the document" className="min-h-[60px]" {...register("description")} />
    </div>

    <div className="space-y-1.5">
      <Label>Source Type</Label>
      <Controller
        control={control}
        name="source_type"
        render={({ field }) => (
          <Select value={field.value} onValueChange={field.onChange}>
            <SelectTrigger><SelectValue placeholder="Select source type" /></SelectTrigger>
            <SelectContent>
              {activeSourceTypes.map((st) => <SelectItem key={st.id} value={st.name}>{st.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      />
    </div>

    <div className="space-y-1.5">
      <Label>Evidence Type</Label>
      <Controller
        control={control}
        name="evidence_type"
        render={({ field }) => (
          <Select value={field.value} onValueChange={field.onChange}>
            <SelectTrigger><SelectValue placeholder="Select evidence type" /></SelectTrigger>
            <SelectContent>
              {activeEvidenceTypes.map((et) => <SelectItem key={et.id} value={et.name}>{et.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      />
    </div>

    <div className="col-span-2 space-y-1.5">
      <Label>Topic(s)</Label>
      <Controller
        control={control}
        name="topic_ids"
        render={({ field }) => (
          <TopicsMultiSelect value={field.value} onChange={field.onChange} />
        )}
      />
    </div>

    <div className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-organization`}>Source Organisation</Label>
      <Input id={`${idPrefix}-organization`} placeholder="Organisation name" {...register("organization")} />
    </div>

    <div className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-country`}>Country</Label>
      <Input id={`${idPrefix}-country`} placeholder="Country" {...register("country")} />
    </div>

    <div className="space-y-1.5">
      <Label>Data Retention</Label>
      <Controller
        control={control}
        name="data_retention"
        render={({ field }) => (
          <DatePicker
            ref={dataRetentionRef}
            value={field.value}
            onChange={field.onChange}
            onValidityChange={setDataRetentionInvalid}
            placeholder="Select retention date..."
          />
        )}
      />
    </div>

    <div className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-url`}>URL</Label>
      <Input id={`${idPrefix}-url`} type="url" placeholder="Document URL" {...register("url")} />
    </div>

    <div className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-language`}>Language</Label>
      <Input id={`${idPrefix}-language`} placeholder="e.g. English" {...register("language")} />
    </div>

    <div className="space-y-1.5">
      <Label>Ownership</Label>
      <Controller
        control={control}
        name="ownership"
        render={({ field }) => (
          <Select
            value={field.value}
            onValueChange={(val) => {
              field.onChange(val);
              if (val !== "Client") {
                setValue("client_id", "");
                setValue("client_name", "");
              }
            }}
            disabled={readOnlyOwnership}
          >
            <SelectTrigger><SelectValue placeholder="Select ownership" /></SelectTrigger>
            <SelectContent>
              {/* Show Public as a read-only option for legacy documents that still carry it */}
              {field.value === "Public" && <SelectItem value="Public">Public</SelectItem>}
              {(allowedOwnershipValues ?? OWNERSHIP_VALUES).map((v: string) => (
                <SelectItem key={v} value={v}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
    </div>

    {/* Client field — shown when ownership = Client */}
    <Controller
      control={control}
      name="ownership"
      render={({ field: ownershipField }) =>
        ownershipField.value === "Client" ? (
          <div className="space-y-1.5">
            <Label>Client</Label>
            {lockedClientName ? (
              <div className="flex h-9 w-full items-center rounded-md border bg-muted px-3 text-sm text-muted-foreground">
                {lockedClientName}
              </div>
            ) : (
              <Controller
                control={control}
                name="client_name"
                render={({ field: clientNameField }) => (
                  <Popover
                    open={clientPopoverOpen}
                    onOpenChange={(open) => {
                      setClientPopoverOpen(open);
                      if (!open) {
                        setClientInputValue("");
                        setClientSearch("");
                        if (clientDebounceRef.current) clearTimeout(clientDebounceRef.current);
                      }
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={clientPopoverOpen}
                        className="w-full justify-between font-normal text-muted-foreground hover:text-muted-foreground hover:bg-inherit"
                      >
                        {clientNameField.value || "Select client..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start" style={{ width: "var(--radix-popover-trigger-width)" }}>
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="Type to search clients..."
                          value={clientInputValue}
                          onValueChange={(val) => {
                            setClientInputValue(val);
                            if (clientDebounceRef.current) clearTimeout(clientDebounceRef.current);
                            clientDebounceRef.current = setTimeout(() => setClientSearch(val), 350);
                          }}
                        />
                        <CommandList>
                          {clientInputValue.trim().length === 0 ? (
                            <CommandEmpty>Start typing to search clients...</CommandEmpty>
                          ) : clientFetching ? (
                            <CommandEmpty>Searching...</CommandEmpty>
                          ) : clientResults.length === 0 ? (
                            <CommandEmpty>No clients found.</CommandEmpty>
                          ) : (
                            <CommandGroup>
                              {clientResults.map((client) => (
                                <CommandItem
                                  key={client.id}
                                  value={client.id}
                                  onSelect={() => {
                                    setValue("client_id", client.id);
                                    clientNameField.onChange(client.name);
                                    setClientPopoverOpen(false);
                                  }}
                                >
                                  <Check className={cn("mr-2 h-4 w-4",
                                    getValues("client_id") === client.id ? "opacity-100" : "opacity-0"
                                  )} />
                                  {client.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              />
            )}
          </div>
        ) : null
      }
    />

    <div className="col-span-2 space-y-1.5">
      <Label htmlFor={`${idPrefix}-tags`}>Tags</Label>
      <Controller
        control={control}
        name="tags"
        render={({ field }) => (
          <TagsInput id={`${idPrefix}-tags`} value={field.value} onChange={field.onChange} placeholder="Add tags, e.g. NCCN, Guidelines, Lung Cancer" />
        )}
      />
    </div>
  </div>
  );
});
DocumentMetadataFields.displayName = "DocumentMetadataFields";

export default DocumentMetadataFields;
